/** Mirrors the wire format from @automation/core. Kept local so the web build stays standalone. */
export type DurationKind = "day" | "week" | "month" | "quarter" | "year" | "custom" | "open";
export type ColumnKind = "backlog" | "active" | "blocked" | "review" | "done";
export type Priority = "low" | "medium" | "high" | "urgent";

/** What a signed-in person may do; mirrors core's `Role`. */
export type Role = "owner" | "admin" | "member" | "viewer";
export type InvitableRole = Exclude<Role, "owner">;

export interface User {
  id: string;
  displayName: string;
  kind: "human" | "agent";
  email: string | null;
  role: Role | null;
  disabled: boolean;
  /** An avatar icon key and tint (`lib/avatars`); null draws initials. Agents always have one. */
  icon: string | null;
  iconColor: string | null;
  createdAt: string;
  /** An agent's `@handle`, when this user is an agent with its own session. */
  handle?: string;
  /**
   * For an agent (and `claude`): every `@token` core resolves to it — handle, id and
   * the unambiguous ways of writing its name (`buildAgentHandleMap`). What the UI
   * treats as a tag, so it paints exactly the mentions core records.
   */
  aliases?: string[];
}

export interface Invite {
  id: string;
  email: string;
  role: InvitableRole;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
}

export interface AuthState {
  needsSetup: boolean;
  user: User | null;
}

const ROLE_RANK: Record<Role, number> = { viewer: 0, member: 1, admin: 2, owner: 3 };
/** Mirrors core's `hasRole`, for hiding what the server would refuse anyway. */
export const hasRole = (user: Pick<User, "role"> | null | undefined, at: Role): boolean =>
  !!user?.role && ROLE_RANK[user.role] >= ROLE_RANK[at];

export const ROLE_LABELS: Record<Role, string> = { owner: "Owner", admin: "Admin", member: "Member", viewer: "Viewer" };

/**
 * A directory on the machine running the board, that work on a card can be
 * carried out *inside*. A card with one resolved against it is a card an
 * `@claude` request can actually fix rather than only discuss.
 */
export interface Project {
  id: string;
  name: string;
  slug: string;
  /** As the board process opens it — inside the container, when it runs in one. */
  path: string;
  /** As the user's own machine names it. The one to show. */
  hostPath: string;
  description: string | null;
  /** Shell run in a parallel run's worktree before its branch is merged into main. */
  mergeCheck: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A folder under one of the mounted project roots. Mirrors core's `ProjectDirectory`. */
export interface ProjectDirectory {
  name: string;
  path: string;
  hostPath: string;
  projectId: string | null;
  isRepo: boolean;
}

export interface ProjectBrowse {
  roots: Array<{ path: string; hostPath: string }>;
  path: string | null;
  hostPath: string | null;
  parent: string | null;
  entries: ProjectDirectory[];
}

/**
 * What deleting a project would take with it.
 *
 * A project has no archived state — the one that existed hid the directory from
 * the pickers while it still held its path, so re-registering it came back as
 * "already registered" naming a row the user could not see. Delete is the only
 * exit, and it deletes the work too, so the dialog reads this first and names it.
 */
export interface ProjectUsage {
  projectId: string;
  /** Boards this is the only project of; deleted with it. */
  boards: { id: string; name: string; taskCount: number; archived: boolean }[];
  /** Boards covering other projects too: they stay and only lose this one (and their default, if it was). */
  detachedBoards: { id: string; name: string; archived: boolean; wasDefault: boolean; inheritingTasks: number }[];
  /** Cards that name this project themselves, on boards that are not deleted. */
  tasks: { id: string; title: string; boardId: string; boardName: string }[];
  totalTasks: number;
  /** Agents working in the directory; deleted with it. */
  agents: { id: string; name: string; handle: string }[];
}

/** One project's footprint on the Projects page. Mirrors core's `ProjectOverview`. */
export interface ProjectOverview {
  projectId: string;
  boardCount: number;
  /** Cards whose effective project is this one. */
  taskCount: number;
  agents: { id: string; name: string; handle: string }[];
  /** Whether the directory is still there. */
  exists: boolean;
}

/** Where a card's project came from: the card names one, or its board does. */
export type ProjectSource = "task" | "board";

/** A card's effective project: its own if it has one, otherwise its board's. */
export interface ResolvedProject {
  id: string;
  name: string;
  slug: string;
  path: string;
  hostPath: string;
  description: string | null;
  via: ProjectSource;
}

export interface Board {
  id: string;
  name: string;
  description: string | null;
  /** Default project for every card on this board. A card may override it. */
  projectId: string | null;
  durationKind: DurationKind;
  startsAt: string;
  /** Null exactly for an `open` board: no deadline. */
  endsAt: string | null;
  archived: boolean;
  /** `kanban` (columns and cards) or `canvas` (a graph of nodes; no columns, no cards of its own). */
  kind: BoardKind;
  createdAt: string;
  updatedAt: string;
}

export type BoardKind = "kanban" | "canvas";

/* ---- canvas boards (mirrors core's canvas types) ---- */
export type CanvasNodeKind = "root" | "group" | "task" | "link";
export type CanvasEdgeKind = "depends" | "relates";
export const CANVAS_DOC_TYPES = ["doc", "sheet", "slides", "figma", "repo", "pdf", "link", "other"] as const;
export type CanvasDocType = (typeof CANVAS_DOC_TYPES)[number];

/** A document a node points at. `icon` is an icon key or an image URL; absent, the type's default. */
export interface CanvasDoc {
  name: string;
  url: string;
  type: CanvasDocType;
  icon?: string | null;
}

/** The live fields of the card a task node links. */
export interface CanvasCard {
  id: string;
  title: string;
  priority: Priority;
  assigneeId: string | null;
  boardId: string;
  boardName: string;
  columnId: string;
  columnName: string;
  columnKind: ColumnKind;
  dueAt: string | null;
  completedAt: string | null;
}

export interface CanvasNode {
  id: string;
  boardId: string;
  kind: CanvasNodeKind;
  /** As stored: null on a task node (the card's title shows) and on the root (the board's name). */
  title: string | null;
  /** What to show. */
  label: string;
  note: string | null;
  /** An avatar icon key, or an emoji. */
  icon: string | null;
  /** An uploaded image, served at /api/canvas/nodes/:id/image. */
  hasImage: boolean;
  imageUrl: string | null;
  taskId: string | null;
  card: CanvasCard | null;
  docs: CanvasDoc[];
  collapsed: boolean;
  x: number | null;
  y: number | null;
  /** An avatar colour key. */
  color: string | null;
  position: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CanvasEdge {
  id: string;
  boardId: string;
  fromNodeId: string;
  toNodeId: string;
  kind: CanvasEdgeKind;
  label: string | null;
  createdAt: string;
}

export interface Canvas {
  board: Board;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

/** One canvas a card sits on, for the card's own view. */
export interface CardCanvasRef {
  boardId: string;
  boardName: string;
  nodeId: string;
}

export interface BoardColumn {
  id: string;
  boardId: string;
  key: string;
  name: string;
  kind: ColumnKind;
  position: number;
  wipLimit: number | null;
  createdAt: string;
}

export interface Task {
  id: string;
  boardId: string;
  columnId: string;
  title: string;
  description: string | null;
  assigneeId: string | null;
  createdBy: string;
  priority: Priority;
  dueAt: string | null;
  position: number;
  completedAt: string | null;
  blockedReason: string | null;
  /**
   * Overrides the board's project for this card. `null` is not "no project" — it
   * means the card inherits whatever its board points at.
   */
  projectId: string | null;
  /** Import key when this card came from somewhere else, e.g. a WhatsApp chat. */
  sourceRef: string | null;
  /** Parts of the codebase the card touches; parallel runs never overlap on one. */
  areas: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TaskWithContext extends Task {
  boardName: string;
  boardEndsAt: string | null;
  columnKey: string;
  columnName: string;
  columnKind: ColumnKind;
  overdue: boolean;
  /** Where this card's work happens, already resolved through its board. */
  project: ResolvedProject | null;
}

/**
 * Mirrors core's `CommentKind`. A human always writes `note`; the rest are Claude
 * narrating a job it was given, and the thread renders them differently because a
 * blocker the user has to scan for is a blocker they will miss.
 */
export type CommentKind = "note" | "progress" | "blocker" | "result";

/** An image on a comment. */
export interface CommentAttachment {
  id: string;
  commentId: string;
  filename: string;
  mime: string;
  bytes: number;
  path: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string;
  /** May be empty when the comment is only images. */
  body: string;
  kind: CommentKind;
  createdAt: string;
  attachments: CommentAttachment[];
}

export type MentionStatus = "pending" | "claimed" | "answered" | "dismissed";

/** An `@claude` in a comment, promoted to a request Claude is expected to act on. */
export interface Mention {
  id: string;
  taskId: string;
  boardId: string;
  commentId: string;
  targetId: string;
  requestedBy: string;
  status: MentionStatus;
  source: "web" | "mcp" | "system";
  claimedAt: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  createdAt: string;
}

export interface MentionWithContext extends Mention {
  body: string;
  request: string;
  requestedByName: string;
  taskTitle: string;
  taskDescription: string | null;
  taskAssigneeId: string | null;
  taskPriority: Priority;
  taskDueAt: string | null;
  taskOverdue: boolean;
  boardName: string;
  boardEndsAt: string | null;
  columnKey: string;
  columnName: string;
  columnKind: ColumnKind;
  /** The directory this request will be carried out in, already resolved. */
  project: ResolvedProject | null;
}

/* ---- draft replies ---- */

/** An email carries a subject line; a Teams message does not. */
export type ResponseChannel = "email" | "chat";
/** `acknowledge` is the reply to send now, `completion` the one for when it is done. */
export type ResponseStage = "acknowledge" | "completion";
/** `sent` is the user recording that *they* sent it — nothing here sends anything. */
export type ResponseStatus = "draft" | "approved" | "sent" | "discarded";
export type ResponseOrigin = "outlook" | "teams" | "manual";
export type ResponseTurnKind = "draft" | "revise" | "edit";
export type ResponseTurnStatus = "pending" | "claimed" | "done" | "failed" | "cancelled";

/** A draft reply owed to one person on one card. */
export interface TaskResponse {
  id: string;
  taskId: string;
  boardId: string;
  channel: ResponseChannel;
  stage: ResponseStage;
  status: ResponseStatus;
  recipientName: string;
  recipientRef: string | null;
  cc: string[];
  /** `null` for a chat message, which has no subject line. */
  subject: string | null;
  body: string;
  source: ResponseOrigin;
  sourceRef: string | null;
  createdBy: string;
  actorSource: "web" | "mcp" | "system";
  revision: number;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One exchange about a draft — the queue and the chat transcript are the same
 * table, because a pending turn is work and a finished one is a message.
 */
export interface ResponseTurn {
  id: string;
  responseId: string | null;
  taskId: string;
  boardId: string;
  kind: ResponseTurnKind;
  instruction: string;
  status: ResponseTurnStatus;
  requestedBy: string;
  actorSource: "web" | "mcp" | "system";
  attempts: number;
  /** Claude's side of the exchange, or why it failed. */
  note: string | null;
  resultSubject: string | null;
  resultBody: string | null;
  claimedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface ResponseWithContext extends TaskResponse {
  taskTitle: string;
  taskDescription: string | null;
  taskSourceRef: string | null;
  taskDueAt: string | null;
  taskCompletedAt: string | null;
  columnKey: string;
  columnName: string;
  columnKind: ColumnKind;
  boardName: string;
  boardEndsAt: string | null;
  /** Whether this is the one to send right now. */
  dueNow: boolean;
  turns: ResponseTurn[];
  activeTurn: ResponseTurn | null;
}

export interface ResponseTurnWithContext extends ResponseTurn {
  taskTitle: string;
  taskDescription: string | null;
  taskSourceRef: string | null;
  taskDueAt: string | null;
  columnKey: string;
  columnName: string;
  columnKind: ColumnKind;
  boardName: string;
  boardEndsAt: string | null;
  requestedByName: string;
  response: TaskResponse | null;
}

export interface TaskResponseSummary {
  responses: ResponseWithContext[];
  activeDraftTurn: ResponseTurn | null;
}

/** Per-card reply counts, carried on the board so cards can be badged. */
export interface BoardResponseCount {
  taskId: string;
  open: number;
  dueNow: number;
  working: boolean;
}

/* ---- cards waiting on the user ---- */

/**
 * Why a card is waiting on the user: an agent's open question about it, a draft
 * reply that is theirs to send, or a flag somebody set by hand. A card can carry
 * several at once. Mirrors core's `WaitingReason`.
 */
export type WaitingReason =
  | {
      kind: "question";
      since: string;
      messageId: string;
      agentId: string;
      agentName: string;
      agentHandle: string;
      body: string;
      options: string[];
    }
  | {
      kind: "reply";
      since: string;
      responseId: string;
      recipientName: string;
      channel: ResponseChannel;
      stage: ResponseStage;
    }
  | { kind: "flag"; since: string; byId: string | null; byName: string | null; note: string | null };

export type WaitingReasonKind = WaitingReason["kind"];

/** One card waiting on the user's answer, across every board. Mirrors core's `WaitingCard`. */
export interface WaitingCard {
  taskId: string;
  title: string;
  boardId: string;
  boardName: string;
  columnId: string;
  columnName: string;
  columnKind: ColumnKind;
  priority: Priority;
  /** The oldest reason's `since`: how long the card has been waiting. */
  since: string;
  /** One line, for a list row. */
  summary: string;
  reasons: WaitingReason[];
}

/** An agent's open question on no listed card (asked in its chat). Mirrors core's `WaitingChatQuestion`. */
export interface WaitingChatQuestion {
  messageId: string;
  agentId: string;
  agentName: string;
  agentHandle: string;
  body: string;
  options: string[];
  since: string;
  taskId: string | null;
}

/** A card in a review-kind column, waiting on the user to accept it. Mirrors core's `WaitingReviewCard`. */
export interface WaitingReviewCard {
  taskId: string;
  title: string;
  boardId: string;
  boardName: string;
  columnId: string;
  columnName: string;
  priority: Priority;
  assigneeId: string | null;
  since: string;
}

/* ---- the board's intake chat ---- */

export type IntakeStatus = "pending" | "claimed" | "done" | "failed" | "cancelled";
/** `text` was decoded at upload and rides in the prompt; the rest is read from disk. */
export type IntakeAttachmentKind = "text" | "image" | "pdf";

export interface IntakeAttachment {
  id: string;
  messageId: string;
  boardId: string;
  filename: string;
  mime: string;
  kind: IntakeAttachmentKind;
  bytes: number;
  path: string;
  text: string | null;
  createdAt: string;
}

/** One turn of the chat: what was pasted, and what came of it. */
export interface IntakeMessage {
  id: string;
  boardId: string;
  instruction: string;
  content: string | null;
  status: IntakeStatus;
  requestedBy: string;
  actorSource: "web" | "mcp" | "system";
  attempts: number;
  /** Claude's reply, or why nothing happened. */
  note: string | null;
  /** Cards this message produced, rendered as chips in the chat. */
  createdTasks: string[];
  claimedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface IntakeMessageWithFiles extends IntakeMessage {
  attachments: IntakeAttachment[];
}

export interface BoardIntakeSummary {
  open: number;
  working: boolean;
  total: number;
  lastMessageAt: string | null;
}

/** A file refused at upload, with the reason to show against it. */
export interface IntakeRejection {
  filename: string;
  reason: string;
}

/* ---- WhatsApp exports ---- */

export type WhatsAppImportStatus = "pending" | "claimed" | "done" | "failed" | "cancelled";
export type WhatsAppMediaKind = "photo" | "video" | "audio" | "sticker" | "document" | "other";

/** A chat as this board knows it, with how far this board has read it. */
export interface WhatsAppChat {
  id: string;
  boardId: string;
  chatKey: string;
  name: string;
  selfName: string | null;
  syncedThrough: string | null;
  lastImportAt: string | null;
  lastStatus: "ok" | "failed" | null;
  lastDetail: string | null;
  imported: number;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppImport {
  id: string;
  boardId: string;
  chatId: string;
  chatName: string;
  chatKey: string;
  filename: string;
  instruction: string;
  readPhotos: boolean;
  status: WhatsAppImportStatus;
  since: string | null;
  windowStart: string;
  through: string;
  cappedFrom: string | null;
  skippedOld: number;
  messageCount: number;
  mediaCount: number;
  remaining: number;
  requestedBy: string;
  actorSource: "web" | "mcp" | "system";
  attempts: number;
  note: string | null;
  createdTasks: string[];
  claimedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

/** A photo shown on a card, with the message it arrived in. */
export interface TaskPhoto {
  mediaId: string;
  filename: string;
  bytes: number;
  sentAt: string | null;
  author: string | null;
  caption: string | null;
  /** `linked` was attached on purpose; `source` is the card's own message. */
  via: "linked" | "source";
}

export interface BoardWhatsAppSummary {
  chats: number;
  open: number;
  working: boolean;
}

export interface ActivityEntry {
  id: number;
  boardId: string | null;
  taskId: string | null;
  actorId: string | null;
  action: string;
  detail: Record<string, unknown> | null;
  source: "web" | "mcp" | "system";
  createdAt: string;
}

export interface BoardStats {
  total: number;
  done: number;
  blocked: number;
  review: number;
  active: number;
  backlog: number;
  overdue: number;
  assignedToMe: number;
  /** Cards held by an agent (a session agent or the master). */
  assignedToAgents: number;
  unassigned: number;
  openMentions: number;
}

export interface BoardWindow {
  startsAt: string;
  /** Null for an open board (no deadline), as are `totalMs` and `remainingMs`. */
  endsAt: string | null;
  label: string;
  totalMs: number | null;
  elapsedMs: number;
  remainingMs: number | null;
  progress: number;
  expired: boolean;
}

export interface BoardDetail {
  board: Board;
  /** The board's default project, resolved. */
  project: Project | null;
  /** Every project the board covers, in order, the default among them. */
  projects: Project[];
  window: BoardWindow;
  columns: BoardColumn[];
  tasks: Task[];
  stats: BoardStats;
  openMentions: MentionWithContext[];
  responses: BoardResponseCount[];
  intake: BoardIntakeSummary;
  whatsapp: BoardWhatsAppSummary;
}

export interface TaskDetail {
  task: Task;
  board: Board;
  column: BoardColumn;
  /** Where work on this card happens, resolved through the board. */
  project: ResolvedProject | null;
  window: BoardWindow;
  comments: TaskComment[];
  openMentions: MentionWithContext[];
  responses: TaskResponseSummary;
  photos: TaskPhoto[];
  /** The canvases this card is a node on (one node per canvas). */
  canvases: CardCanvasRef[];
  overdue: boolean;
}

export const DURATION_LABELS: Record<DurationKind, string> = {
  day: "One day",
  week: "One week",
  month: "One month",
  quarter: "One quarter",
  year: "One year",
  custom: "Custom end date",
  open: "No deadline",
};

export const COLUMN_KIND_LABELS: Record<ColumnKind, string> = {
  backlog: "Backlog",
  active: "In progress",
  blocked: "Blocked",
  review: "Needs review",
  done: "Done",
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

export const MENTION_STATUS_LABELS: Record<MentionStatus, string> = {
  pending: "Waiting for Claude",
  claimed: "Claude is on it",
  answered: "Answered",
  dismissed: "Not actioned",
};

export const RESPONSE_CHANNEL_LABELS: Record<ResponseChannel, string> = { email: "Email", chat: "Teams" };

/** Says what the stage *means*, not what it is called — the user never picked it. */
export const RESPONSE_STAGE_LABELS: Record<ResponseStage, string> = {
  acknowledge: "Send now",
  completion: "When it's done",
};

export const RESPONSE_STAGE_HINTS: Record<ResponseStage, string> = {
  acknowledge: "Confirms you have it and says what happens next.",
  completion: "Reports the outcome. Comes due when this card reaches a done state.",
};

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  draft: "Draft",
  approved: "Ready to send",
  sent: "Sent",
  discarded: "Discarded",
};

/** Stage drives the accent, so the two kinds of reply are told apart at a glance. */
export const stageColor = (stage: ResponseStage): string =>
  stage === "acknowledge" ? "var(--kind-active)" : "var(--kind-done)";

export const kindColor = (kind: ColumnKind): string => `var(--kind-${kind})`;
export const priorityColor = (priority: Priority): string => `var(--prio-${priority})`;

/* ------------------------------------------------------------------ agents */

/** `waiting` = waiting on the user; `stopped` = paused by the user. */
export type AgentStatus = "idle" | "working" | "waiting" | "stopped";

export type AgentKind = "session" | "master" | "foreign";

export interface Agent {
  id: string;
  name: string;
  handle: string;
  /**
   * `session` works in one project; `master` (at most one) runs the whole app for the user;
   * `foreign` is Claude Code on someone's own machine, reached through `board-link`.
   */
  kind: AgentKind;
  /** Null for the master, and for a foreign agent, whose directory is on its machine. */
  projectId: string | null;
  workdir: string | null;
  instructions: string | null;
  model: string | null;
  /** Runs instead while `model` is at its usage limit; null is no fallback. */
  fallbackModel: string | null;
  /** `model` hit its usage limit and is skipped until this reset time; null or past = primary. */
  primaryLimitedUntil: string | null;
  allowedTools: string | null;
  status: AgentStatus;
  /** Why a `stopped` agent is paused: a person, the usage limit, or sign-in; null otherwise. */
  pausedReason: "user" | "limit" | "sign-in" | null;
  /** A limit pause's reset: it resumes by itself once this passes. */
  pausedUntil: string | null;
  sessionId: string | null;
  currentTurnId: string | null;
  /** Input tokens the model saw on its latest call: the session's context size now. */
  contextTokens: number;
  /** Compact past this; null uses the worker default (`effectiveContextLimit` has the number). */
  contextLimit: number | null;
  /** The summary a fresh session starts from after a compaction. */
  memory: string | null;
  compactions: number;
  /** Assigned cards that may run at once, each in its own worktree. 1 = main session only. */
  maxParallel: number;
  lastActivityAt: string | null;
  /** When it was pinned under the master in the list; null when it is not. */
  pinnedAt: string | null;
  /** The list folder it is grouped in; null when loose. Never set on the master. */
  folderId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** A folder on the Agents list. Holds at least one agent; the last one out deletes it. */
export interface AgentFolder {
  id: string;
  name: string;
  position: number;
  createdAt: string;
  agentIds: string[];
}

/** What one window of a saved layout shows: a `SidebarView`, minus the retired `archive`. */
export type SavedLayoutView =
  | { kind: "board"; boardId: string }
  | { kind: "agents"; agentId?: string }
  | { kind: "status" | "queue" | "waiting" | "apps" | "activity" | "projects" | "changelog" | "settings" | "boards" | "terminal" };
/** A saved layout's tree: the shape of `lib/workspace.ts`'s `LayoutNode`. */
export type SavedLayoutNode =
  /** `tabs`: a tab group, its views in strip order with `view` (the showing one) among them; only with two or more. */
  | { type: "window"; id: string; view: SavedLayoutView; tabs?: SavedLayoutView[]; minimized?: boolean }
  | { type: "split"; dir: "row" | "column"; sizes: number[]; children: SavedLayoutNode[] };
export interface SavedLayoutNav {
  dock: "left" | "right" | "top" | "bottom";
  hidden: boolean;
}
/** A named arrangement of windows (`workspace_layouts`), one list for the installation. */
export interface WorkspaceLayout {
  id: string;
  name: string;
  layout: SavedLayoutNode;
  /** Where the navigation goes; null leaves the device's own alone. */
  nav: SavedLayoutNav | null;
  position: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type McpServerKind = "connector" | "http" | "stdio";

/** An MCP server attached to an agent: who it is and how much of it, never a definition or a secret. */
export interface AgentMcpServer {
  id: string;
  name: string;
  icon: string;
  kind: McpServerKind;
  description: string | null;
  /** The tools of it the agent may use; empty means all of them. */
  tools: string[];
  /** How many tools the server's catalogue lists; 0 when unknown. */
  knownTools: number;
}

/** A registered MCP server as admins see it: `env` and `headers` are write-only, so only their key names. */
/** A machine agents may SSH into (core's `RemoteMachine`). Never a key. */
export interface RemoteMachine {
  id: string;
  name: string;
  host: string;
  port: number;
  user: string;
  notes: string | null;
  role: string | null;
  capabilities: string[];
  /** Agent ids allowed to use it (advisory); null is every agent. */
  agents: string[] | null;
  keyKind: string;
  keyUpdatedAt: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  sync: RemoteMachineSync | null;
}

export interface RemoteMachineSync {
  ok: boolean;
  error: string | null;
  updatedAt: string;
  /** `SHA256:… (ED25519)` as ssh-keygen gives it, once the host has loaded the key. */
  fingerprint?: string | null;
}

export interface RemoteMachinesView {
  machines: RemoteMachine[];
  host: {
    publicKey: string;
    publicKeyId: string;
    dir: string;
    reservedNames: string[];
    syncedAt: string | null;
    machines: Record<string, RemoteMachineSync>;
    observedAt: string;
    stale: boolean;
  } | null;
}

export interface McpServer {
  id: string;
  name: string;
  icon: string;
  kind: McpServerKind;
  description: string | null;
  url: string | null;
  command: string | null;
  args: string[];
  envKeys: string[];
  headerKeys: string[];
  tools: string[];
  agentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AgentWithContext extends Agent {
  mcpServers: AgentMcpServer[];
  /** Avatar icon and tint keys (`lib/avatars.tsx`). */
  icon: string | null;
  iconColor: string | null;
  /** Null for the master agent. */
  project: Pick<Project, "id" | "name" | "slug" | "path" | "hostPath"> | null;
  /** Where its runs start: its project's directory, or the master's workspace. */
  directory: { path: string; hostPath: string };
  pendingTurns: number;
  /** The card the running turn is about, if it is about one. */
  currentTaskId: string | null;
  /** Stop was pressed on the running turn and the worker has not killed it yet. */
  stopping: boolean;
  /** A run of its is paused (or pausing) for a deploy and will resume on the new worker. */
  pausedForDeploy: boolean;
  /**
   * The running turn holds its stdin open, so a message typed now reaches the run
   * under way rather than queueing behind it. False with no running turn, or a
   * parallel/foreign/host-side run, or one past its context cap.
   */
  liveInputOpen: boolean;
  /** Parallel-lane runs under way now, each in its own worktree. */
  parallelRuns: ParallelRun[];
  /** `contextLimit`, or the default when it is null. */
  effectiveContextLimit: number;
  lastMessage: Pick<AgentMessage, "role" | "kind" | "body" | "createdAt"> | null;
  /** Pinned above the rest: the master always, others once pinned. */
  pinned: boolean;
  /** Newest of its last message, last turn and creation: the row's time and the unpinned order. */
  activityAt: string;
  /** Every question still open, oldest first — what "needs you" is about. Empty for non-admins. */
  openQuestions: AgentOpenQuestion[];
  /** A foreign agent's link to its machine; null for every other kind. */
  link: AgentLinkInfo | null;
}

/**
 * How a foreign agent's machine is connected (core's `AgentLinkInfo`). `online` is the API
 * holding its socket with a recent heartbeat; turns for an offline one wait.
 */
export interface AgentLinkInfo {
  /** A token has been issued and not revoked. */
  paired: boolean;
  online: boolean;
  /** What the CLI reported at its last connect. */
  machine: string | null;
  platform: string | null;
  version: string | null;
  /** The directory `board-link run` works in, on that machine. */
  dir: string | null;
  seenAt: string | null;
  connectedAt: string | null;
  disconnectedAt: string | null;
  /** The one board it is confined to; null is every board. */
  boardId: string | null;
  /** A pairing code waiting to be redeemed, until this time; null when none is. */
  pairingExpiresAt: string | null;
}

/** A fresh pairing code for `board-link pair`, shown once. */
export interface AgentPairingCode {
  code: string;
  expiresAt: string;
}

/** An open question as the agent list carries it. */
export interface AgentOpenQuestion {
  messageId: string;
  body: string;
  options: string[];
  createdAt: string;
  taskId: string | null;
  taskTitle: string | null;
}

/** One parallel-lane run in progress. */
export interface ParallelRun {
  turnId: string;
  taskId: string | null;
  branch: string | null;
  worktreePath: string | null;
  claimedAt: string | null;
  stopping: boolean;
}

export type AgentTurnTrigger = "chat" | "answer" | "mention" | "assignment" | "webhook" | "cron" | "compact" | "merge" | "ship";
export type AgentTurnLane = "main" | "parallel";
export type AgentMergeStatus = "merged" | "nothing" | "failed";
export type AgentTurnStatus = "pending" | "claimed" | "done" | "failed" | "cancelled";

export interface AgentTurn {
  id: string;
  agentId: string;
  trigger: AgentTurnTrigger;
  status: AgentTurnStatus;
  summary: string;
  taskId: string | null;
  mentionId: string | null;
  triggerId: string | null;
  requestedBy: string;
  depth: number;
  attempts: number;
  note: string | null;
  sessionId: string | null;
  costUsd: number | null;
  claimedAt: string | null;
  finishedAt: string | null;
  stopRequestedAt: string | null;
  stopRequestedBy: string | null;
  lane: AgentTurnLane;
  worktreePath: string | null;
  branch: string | null;
  mergeStatus: AgentMergeStatus | null;
  mergeNote: string | null;
  mergedSha: string | null;
  shipTurnId: string | null;
  interruptedAt: string | null;
  resumeCount: number;
  deployPauseAt: string | null;
  pausedClean: boolean;
  createdAt: string;
}

export type AgentMessageRole = "user" | "agent" | "system";
export type AgentMessageKind = "text" | "tool" | "error" | "status" | "question" | "suggestion";
export type AgentAskStatus = "open" | "answered" | "accepted" | "dismissed";

export interface AgentAttachment {
  id: string;
  messageId: string;
  filename: string;
  mime: string;
  /** `file`: anything not text, image or PDF — stored as it came, downloaded rather than shown. */
  kind: "text" | "image" | "pdf" | "file";
  bytes: number;
  path: string;
  /**
   * `/files/<token>/<filename>`, absolute when the board knows its public address.
   * Private unless `shared`: a signed-in admin downloads from it, anyone else gets a
   * 404. Null when the file has no link (one the user sent) or is deleted.
   */
  url: string | null;
  /** Made public by an admin: the link works without signing in. */
  shared: boolean;
  /** Deleted once a person has downloaded it. */
  deleteAfterDownload: boolean;
  /** The bytes are gone (deleted by the agent, or downloaded once). */
  deletedAt: string | null;
}

export interface AgentMessage {
  attachments?: AgentAttachment[];
  id: string;
  agentId: string;
  turnId: string | null;
  role: AgentMessageRole;
  kind: AgentMessageKind;
  authorId: string | null;
  body: string;
  data: Record<string, unknown> | null;
  taskId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const AGENT_STATUS_LABELS: Record<AgentStatus, string> = {
  idle: "Idle",
  working: "Working",
  waiting: "Waiting on you",
  stopped: "Paused",
};

/**
 * Reuses the column-kind palette: an agent's states mean what those columns mean —
 * bar waiting on the user, which is the one colour everything waiting on them
 * shares (`--waiting`: cards, the Awaiting page, questions in a chat).
 */
export const agentStatusColor = (status: AgentStatus): string =>
  status === "working"
    ? "var(--kind-active)"
    : status === "waiting"
      ? "var(--waiting)"
      : status === "stopped"
        ? "var(--kind-backlog)"
        : "var(--kind-done)";

/** Something that wakes an agent without a person asking: a webhook hit or a schedule. */
export interface AgentTrigger {
  id: string;
  agentId: string;
  name: string;
  kind: "webhook" | "cron";
  instruction: string;
  /** Five-field cron, for kind=cron. */
  schedule: string | null;
  /** IANA zone the schedule is read in. */
  timezone: string | null;
  nextRunAt: string | null;
  enabled: boolean;
  fireCount: number;
  lastFiredAt: string | null;
  lastResult: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ status */

export type ClaudeUsageSource = "agent" | "compact" | "responses" | "intake" | "whatsapp";

export interface UsageTotals {
  runs: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  /** What the CLI reports the run cost at list price; on a subscription plan nobody is billed it. */
  costUsd: number;
}

export interface UsagePeriod {
  total: UsageTotals;
  /** `agentId` null is the worker's own runs (reply rewrites, intake, imports). */
  byAgent: Array<UsageTotals & { agentId: string | null; name: string; handle: string | null }>;
  byProject: Array<UsageTotals & { projectId: string | null; name: string }>;
  /** `account` null: runs from before the account was recorded. */
  byAccount: Array<UsageTotals & { account: string | null }>;
}

/** One of the plan's windows, from the CLI's `rate_limit_event`. */
export interface RateLimitWindow {
  /** 0–1 of the window used. */
  utilization: number | null;
  resetsAt: string | null;
}

export interface RateLimitStatus {
  /** `allowed`, `allowed_warning`, `rejected` … as the CLI said it. */
  status: string | null;
  /** Which window the status is about (`five_hour`, `seven_day` …). */
  rateLimitType: string | null;
  resetsAt: string | null;
  windows: Record<string, RateLimitWindow>;
  isUsingOverage: boolean | null;
  overageStatus: string | null;
  /** The Claude account whose run reported it: each account has its own limits. */
  account?: string | null;
}

/**
 * One of an account's plan limits as Anthropic's usage API reports it — what the
 * CLI's `/usage` shows, model-scoped weekly limits (Fable) included, which a run's
 * `rate_limit_event` never carries.
 */
export interface PlanLimit {
  /** `session`, `weekly_all`, `weekly_scoped` …, or a window name of the older shape. */
  kind: string;
  label: string;
  /** Used, 0–1; null when not reported. */
  utilization: number | null;
  resetsAt: string | null;
  /** The model a scoped limit is about ("Fable"); null for the plan-wide ones. */
  model: string | null;
  severity: string | null;
}

/** An account's limits as last read; `error` says why there is no reading (a token that cannot read usage). */
export interface AccountPlanUsage {
  observedAt: string | null;
  limits: PlanLimit[];
  error: string | null;
}

/** Runs on one account refused at sign-in (a bad or expired token), counted until it is fixed. */
export interface ClaudeSignInFailure {
  account: string;
  count: number;
  firstAt: string;
  lastAt: string;
  message: string;
}

/** Per account: its plan limits, its last run's windows, and what Settings knows of it. */
export interface StatusClaudeAccount {
  name: string;
  active: boolean;
  plan: AccountPlanUsage | null;
  lastRun: (RateLimitStatus & { observedAt: string }) | null;
}

export interface ContainerStatus {
  name: string;
  /** `running`, `exited`, `missing` … */
  state: string;
  /** Docker's own words: "Up 3 hours (healthy)". */
  status: string;
  restartCount: number | null;
}

export interface HostStatus {
  hostname: string;
  cpuPercent: number | null;
  cpuCount: number;
  loadAverage: [number, number, number];
  memory: { usedBytes: number; totalBytes: number };
  disk: { path: string; usedBytes: number; totalBytes: number } | null;
  uptimeSeconds: number;
  worker: { name: string; status: string; restarts: number; uptimeSeconds: number | null; memoryBytes: number | null } | null;
  containers: ContainerStatus[];
}

/** One process an app runs: a docker container or a pm2 process. */
export interface AppProcess {
  kind: "container" | "pm2";
  name: string;
  /** The compose service (`web`, `db`), or null for pm2 and a bare container. */
  service: string | null;
  /** Docker's `running`/`exited`/`restarting`…, pm2's `online`/`stopped`/`errored`. */
  state: string;
  /** Docker's healthcheck verdict (`healthy`, `unhealthy`, `starting`), when it has one. */
  health: string | null;
  /** When the process last started, while it is up. */
  startedAt: string | null;
  restarts: number | null;
  cpuPercent: number | null;
  memoryBytes: number | null;
  /** The image for a container, the script for pm2. */
  image: string | null;
  /** Published ports as `host:port→port/proto`, exposed-only ones as `port/proto`. */
  ports: string[];
  /** The compose project's folder or pm2's cwd — what the process is grouped by. */
  path: string | null;
}

/**
 * One app: every process under one top-level folder of the apps root, so an app's
 * web, database and cache — and a pm2 worker in a subfolder — read as one thing.
 */
export interface AppGroup {
  key: string;
  name: string;
  /** The folder the group is, or null when it came from a compose project name alone. */
  path: string | null;
  /** From the containers' `apps.git.repo` / `apps.git.branch` labels, when set. */
  repo: string | null;
  branch: string | null;
  /** From Traefik `Host(…)` rules in the containers' labels, when any. */
  domains: string[];
  processes: AppProcess[];
}

/**
 * The Claude accounts agent runs can sign in as, as the worker last read the host's
 * store: names only, never a token. `pending` is a switch asked here and not yet applied.
 */
export interface ClaudeAccountsView {
  active: string;
  accounts: Array<{ name: string; active: boolean; added: string | null }>;
  lastSwitch: { name: string; ok: boolean; error: string | null; by: string | null; at: string } | null;
  checking?: { name: string; since: string } | null;
  signInFailure?: ClaudeSignInFailure | null;
  observedAt: string;
  stale: boolean;
  pending: { name: string; requestedBy: string; requestedAt: string; force?: boolean } | null;
}

export type EngineType = "claude" | "agy";

export interface EngineModelOption {
  id: string;
  name: string;
  family?: string;
  description?: string;
}

/** The worker's reading of the Antigravity CLI on the host (`status_snapshots.agy_host`). */
export interface AgyHostStatus {
  installed: boolean;
  bin: string | null;
  version: string | null;
  /** `agy models` answered: the login works. */
  signedIn: boolean;
  /** Why it is not usable, in words for Settings. */
  error: string | null;
  models: EngineModelOption[];
}

export interface EngineSettings {
  activeEngine: EngineType;
  /** Empty: the CLI's own default model. */
  agyModel: string;
  /** Empty: the worker's default (`AGENT_MODEL`, else the CLI's). */
  claudeModel: string;
  /** Installed, signed in and reported on recently: choosing it would work. */
  agyAvailable: boolean;
  /** Why it cannot be chosen, when it cannot. */
  agyProblem: string | null;
  agyInstalled: boolean;
  agySignedIn: boolean;
  agyBin?: string;
  agyVersion: string | null;
  agyCheckedAt: string | null;
  agyModels: EngineModelOption[];
  claudeModels: EngineModelOption[];
  updatedAt: string | null;
}

/** What the worker saw of every app on the host. Read-only: nothing here starts or stops. */
export interface AppsStatus {
  /** The folder apps are grouped under (`STATUS_APPS_ROOT`, default `~/apps`). */
  root: string | null;
  apps: AppGroup[];
  /** What could not be read (docker unreachable, no pm2), in words. */
  errors: string[];
}

export interface StatusDashboard {
  generatedAt: string;
  timezone: string;
  usage: Record<"today" | "week" | "month", UsagePeriod>;
  /** The last 14 days in `timezone`, days with no runs left out. */
  daily: Array<{ day: string; costUsd: number; tokens: number; runs: number }>;
  /** Null until a run has streamed a `rate_limit_event`. */
  rateLimit: (RateLimitStatus & { observedAt: string }) | null;
  /** Agents whose model is at its usage limit, running on their fallback until `until`. */
  onFallback: Array<{ agentId: string; name: string; model: string | null; fallbackModel: string; until: string }>;
  /** Every Claude account the worker knows, the active one first, and a sign-in failure to shout about. */
  claudeAccounts: { active: string | null; accounts: StatusClaudeAccount[]; signInFailure: ClaudeSignInFailure | null };
  activity: {
    running: Array<{
      turnId: string;
      agentId: string;
      handle: string | null;
      name: string;
      summary: string;
      taskId: string | null;
      claimedAt: string | null;
      /** `parallel`: a card in its own worktree, beside the agent's main session. */
      lane: AgentTurnLane;
      branch: string | null;
      stopping: boolean;
    }>;
    queued: { agentTurns: number; responseTurns: number; intakeMessages: number; whatsappImports: number };
    /** `AGENT_MAX_PROCS`: the cap on parallel claims. */
    maxProcs: number;
  };
  /** Null until the worker has written a reading; `stale` once it stops. */
  host: (HostStatus & { observedAt: string; stale: boolean }) | null;
}

/** What `GET /api/voice` says the server can do: local whisper.cpp dictation, when installed. */
export interface VoiceEngines {
  stt: boolean;
}

/** Mirrors core's `ProjectEnvFile`: an env file's key names and state, never its values. */
export type EnvGitState = "tracked" | "ignored" | "not-ignored" | "no-repo" | "unknown";
export interface ProjectEnvFile {
  filename: string;
  size: number;
  modifiedAt: string;
  mode: number;
  keys: string[];
  git: EnvGitState;
  lastChange: { at: string; actorId: string | null; action: "written" | "deleted" } | null;
}
export interface ProjectEnvStatus {
  projectId: string;
  projectName: string;
  hostPath: string;
  files: ProjectEnvFile[];
}
export interface WriteProjectEnvResult {
  file: ProjectEnvFile;
  created: boolean;
  mode: "replace" | "merge";
  keysWritten: string[];
  invalidLines: number[];
}

/** Mirrors core's NOTIFICATION_KINDS / NotificationPrefs (services/notifications.ts). */
export type NotificationKind = "waiting" | "agent_stopped" | "card_resolved" | "agent_running";
export type NotificationPrefs = Record<NotificationKind, boolean>;

/** One browser subscribed to this person's push. Mirrors core's `PushDevice`. */
export interface PushDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  failedAt: string | null;
  service: string;
}

/** How a preview is reached: by anyone holding the link, or only by someone signed in to the board. */
export type PreviewAccess = "link" | "signed-in";
export type PreviewClosedReason = "expired" | "agent" | "user" | "restart-gone";

/** An agent's dev server exposed at `url` until `expiresAt` (core `services/previews.ts`). */
export interface Preview {
  id: string;
  agentId: string;
  agentHandle: string;
  projectId: string | null;
  slug: string;
  host: string;
  url: string;
  port: number;
  title: string | null;
  access: PreviewAccess;
  createdAt: string;
  expiresAt: string;
  closedAt: string | null;
  closedReason: PreviewClosedReason | null;
  lastHitAt: string | null;
  hits: number;
  /** Not closed and not past its expiry. */
  open: boolean;
}
